import React, { useState, useEffect } from 'react';
import { collection, query, getDocs, addDoc, serverTimestamp, deleteDoc, doc } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../../../firebase/config';
import { useSelector } from 'react-redux';
import { sendDocumentUploadNotification } from '../../../emailjs/emailService';
import { 
  FaFileUpload, 
  FaFileDownload, 
  FaFolder, 
  FaFilePdf, 
  FaFileImage, 
  FaFileAlt,
  FaSpinner,
  FaSearch,
  FaTrash,
  FaTimes,
  FaPlus,
  FaEnvelope
} from 'react-icons/fa';
import './index.scss';

const Documents = () => {
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [category, setCategory] = useState('ID & Visa');
  const [searchQuery, setSearchQuery] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [showUploadPopup, setShowUploadPopup] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [sendNotification, setSendNotification] = useState(true);
  
  // Get all employees from redux store instead of fetching
  const { user } = useSelector(state => state.auth);
  const { employees } = useSelector(state => state.auth);
  
  useEffect(() => {
    // Fetch documents for the selected employee
    if (selectedEmployee) {
      fetchEmployeeDocuments(selectedEmployee.email);
    } else {
      setDocuments([]);
    }
  }, [selectedEmployee]);
  
  const fetchEmployeeDocuments = async (email) => {
    setIsLoading(true);
    try {
      const documentsRef = collection(db, 'otherDBs', 'documentsManager', 'documentsManager');
      const q = query(documentsRef);
      
      const querySnapshot = await getDocs(q);
      const docs = [];
      
      querySnapshot.forEach(doc => {
        const data = doc.data();
        if (data.employeeEmail === email) {
          docs.push({
            id: doc.id,
            ...data
          });
        }
      });
      
      setDocuments(docs);
    } catch (error) {
      console.error('Error fetching documents:', error);
      setErrorMessage('Failed to load documents. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleEmployeeSelect = (emp) => {
    setSelectedEmployee(emp);
    setSearchQuery('');
    setSuccessMessage('');
    setErrorMessage('');
  };
  
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setSelectedFile(file);
  };
  
  const openUploadPopup = () => {
    if (!selectedEmployee) {
      setErrorMessage('Please select an employee first');
      return;
    }
    setShowUploadPopup(true);
    setSuccessMessage('');
    setErrorMessage('');
  };
  
  const closeUploadPopup = () => {
    setShowUploadPopup(false);
    setSelectedFile(null);
    setCategory('ID & Visa');
  };
  
  const handleFileUpload = async () => {
    if (!selectedFile) {
      setErrorMessage('Please select a file to upload');
      return;
    }
    
    if (!selectedEmployee) {
      setErrorMessage('Please select an employee first');
      closeUploadPopup();
      return;
    }
    
    setIsUploading(true);
    setUploadProgress(0);
    setSuccessMessage('');
    setErrorMessage('');
    
    try {
      // Create a reference to the file in Firebase Storage
      const fileExtension = selectedFile.name.split('.').pop();
      const fileName = `${selectedEmployee.employeeId || Date.now().toString(36)} - ${selectedFile.name}`;
      const storageRef = ref(storage, `documents/${selectedEmployee.employeeId || selectedEmployee.email}/${fileName}`);
      
      // Upload the file
      const uploadTask = uploadBytesResumable(storageRef, selectedFile);
      
      // Register three observers:
      // 1. 'state_changed' observer, called any time the state changes
      // 2. Error observer, called on failure
      // 3. Completion observer, called on successful completion
      uploadTask.on('state_changed', 
        (snapshot) => {
          // Observe state change events such as progress, pause, and resume
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(progress);
        }, 
        (error) => {
          // Handle unsuccessful uploads
          console.error('Error uploading file:', error);
          setErrorMessage('File upload failed. Please try again.');
          setIsUploading(false);
        }, 
        async () => {
          // Handle successful uploads on complete
          try {
            // Get download URL
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            
            // Document metadata
            const documentData = {
              employeeEmail: selectedEmployee.email,
              name: selectedEmployee.name,
              category,
              fileURL: downloadURL,
              fileRefName: fileName,
              fileType: fileExtension.toLowerCase(),
              createdAt: serverTimestamp(),
              uploadedBy: user.email || 'admin'
            };
            
            // Add document metadata to Firestore
            const documentRef = collection(db, 'otherDBs', 'documentsManager', 'documentsManager');
            const docRef = await addDoc(documentRef, documentData);
            
            // Send email notification
            if (sendNotification) {
              try {
                await sendDocumentUploadNotification(
                  selectedEmployee, 
                  {
                    ...documentData,
                    id: docRef.id,
                    // Provide client-side date since serverTimestamp is not available in the client
                    createdAt: new Date()
                  }
                );
                console.log('Document upload notification sent successfully');
                setSuccessMessage('Document uploaded successfully and notification sent!');
              } catch (emailError) {
                console.error('Error sending document notification:', emailError);
                // Don't fail the upload if email sending fails
                setSuccessMessage('Document uploaded successfully but notification email could not be sent.');
              }
            } else {
              setSuccessMessage('Document uploaded successfully!');
            }
            
            // Refresh the documents list
            fetchEmployeeDocuments(selectedEmployee.email);
            
            closeUploadPopup();
          } catch (error) {
            console.error('Error saving document metadata:', error);
            setErrorMessage('Failed to save document information. Please try again.');
          } finally {
            setIsUploading(false);
            setUploadProgress(0);
          }
        }
      );
    } catch (error) {
      console.error('Error initiating upload:', error);
      setErrorMessage('Failed to start upload. Please try again.');
      setIsUploading(false);
    }
  };
  
  const handleDeleteDocument = async (document) => {
    setConfirmDelete(document);
  };
  
  const confirmDeleteDocument = async () => {
    if (!confirmDelete) return;
    
    setIsDeleting(true);
    setSuccessMessage('');
    setErrorMessage('');
    
    try {
      // Delete document from Firestore
      const docRef = doc(db, 'otherDBs', 'documentsManager', 'documentsManager', confirmDelete.id);
      await deleteDoc(docRef);
      
      // Delete file from Storage if we have a URL
      if (confirmDelete.fileURL) {
        try {
          // Extract the path from the URL
          const employeeFolder = `documents/${selectedEmployee.employeeId || selectedEmployee.email}`;
          const storageRef = ref(storage, `${employeeFolder}/${confirmDelete.fileRefName}`);
          await deleteObject(storageRef);
        } catch (storageError) {
          console.error('Error deleting file from storage:', storageError);
          // Continue anyway since the document is deleted from Firestore
        }
      }
      
      // Update the documents list
      setDocuments(prev => prev.filter(doc => doc.id !== confirmDelete.id));
      setSuccessMessage('Document deleted successfully!');
    } catch (error) {
      console.error('Error deleting document:', error);
      setErrorMessage('Failed to delete document. Please try again.');
    } finally {
      setIsDeleting(false);
      setConfirmDelete(null);
    }
  };
  
  const cancelDeleteDocument = () => {
    setConfirmDelete(null);
  };
  
  const handleDownload = (url) => {
    window.open(url, '_blank');
  };
  
  const getFileIcon = (fileType) => {
    switch(fileType.toLowerCase()) {
      case 'pdf':
        return <FaFilePdf className="file-icon pdf" />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return <FaFileImage className="file-icon image" />;
      default:
        return <FaFileAlt className="file-icon default" />;
    }
  };
  
  const filteredDocuments = documents.filter(doc => {
    if (!searchQuery) return true;
    
    const query = searchQuery.toLowerCase();
    return (
      doc.fileRefName.toLowerCase().includes(query) ||
      doc.category.toLowerCase().includes(query)
    );
  });
  
  const filterByCategory = (category) => {
    return filteredDocuments.filter(doc => doc.category === category);
  };
  
  return (
    <div className="documents-page">
      <div className="page-header">
        <h1>Documents Management</h1>
        <p>Upload, organize, and manage employee documents and files.</p>
      </div>
      
      {errorMessage && (
        <div className="alert error">
          {errorMessage}
        </div>
      )}
      
      {successMessage && (
        <div className="alert success">
          {successMessage}
        </div>
      )}
      
      {confirmDelete && (
        <div className="delete-confirmation">
          <div className="delete-dialog">
            <h3>Confirm Delete</h3>
            <p>Are you sure you want to delete "{confirmDelete.fileRefName}"?</p>
            <p>This action cannot be undone.</p>
            
            <div className="dialog-actions">
              <button 
                className="cancel-button" 
                onClick={cancelDeleteDocument}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button 
                className="delete-button" 
                onClick={confirmDeleteDocument}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <FaSpinner className="spinner" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <FaTrash />
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {showUploadPopup && (
        <div className="upload-popup">
          <div className="upload-dialog">
            <div className="popup-header">
              <h3>Upload Document for {selectedEmployee.name}</h3>
              <button className="close-button" onClick={closeUploadPopup}>
                <FaTimes />
              </button>
            </div>
            
            <div className="popup-content">
              <div className="form-group">
                <label>Document Category</label>
                <select 
                  className="category-select"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  disabled={isUploading}
                >
                  <option value="ID & Visa">ID & Visa</option>
                  <option value="Employment Document">Employment Document</option>
                  <option value="Miscellaneous">Miscellaneous</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>Document File</label>
                <div className="file-input-wrapper">
                  <label className="file-input-label">
                    <span>{selectedFile ? selectedFile.name : 'Choose a file...'}</span>
                    <input 
                      type="file" 
                      onChange={handleFileChange}
                      disabled={isUploading}
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    />
                  </label>
                  <button 
                    className="browse-button"
                    onClick={() => document.querySelector('.file-input-label input').click()}
                    disabled={isUploading}
                  >
                    Browse
                  </button>
                </div>
              </div>
              
              <div className="form-group notification-option">
                <label className="checkbox-label">
                  <input 
                    type="checkbox" 
                    checked={sendNotification} 
                    onChange={() => setSendNotification(!sendNotification)}
                    disabled={isUploading}
                  />
                  <span>Send notification email to {selectedEmployee.name}</span>
                </label>
                <div className="notification-help">
                  <FaEnvelope className="envelope-icon" />
                  <span>Employee will receive an email with document details</span>
                </div>
              </div>
              
              {isUploading && (
                <div className="upload-progress">
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                  <span>{Math.round(uploadProgress)}% Uploaded</span>
                </div>
              )}
            </div>
            
            <div className="popup-actions">
              <button 
                className="cancel-button" 
                onClick={closeUploadPopup}
                disabled={isUploading}
              >
                Cancel
              </button>
              <button 
                className="upload-button" 
                onClick={handleFileUpload}
                disabled={isUploading || !selectedFile}
              >
                {isUploading ? (
                  <>
                    <FaSpinner className="spinner" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <FaFileUpload />
                    Upload Document
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div className="documents-container">
        {/* Employee Sidebar */}
        <div className="employees-sidebar">
          <div className="sidebar-header">
            <h2>Employees</h2>
            <div className="search-input">
              <FaSearch className="search-icon" />
              <input 
                type="text" 
                placeholder="Search employees..." 
                onChange={(e) => setSearchQuery(e.target.value)}
                value={searchQuery}
              />
            </div>
          </div>
          
          <div className="employees-list">
            {!employees || employees.length === 0 ? (
              <div className="no-employees">
                <p>No employees found.</p>
              </div>
            ) : (
              employees
                .filter(emp => emp.name?.toLowerCase().includes(searchQuery.toLowerCase()) || 
                              emp.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                              emp.employeeId?.toLowerCase().includes(searchQuery.toLowerCase()))
                .map(emp => (
                  <div 
                    key={emp.id} 
                    className={`employee-item ${selectedEmployee?.id === emp.id ? 'selected' : ''}`}
                    onClick={() => handleEmployeeSelect(emp)}
                  >
                    <div className="employee-avatar">
                      {emp.name ? emp.name.charAt(0).toUpperCase() : 'U'}
                    </div>
                    <div className="employee-info">
                      <h3>{emp.name || 'Unknown'}</h3>
                      <p>{emp.email}</p>
                      {emp.employeeId && <span className="employee-id">ID: {emp.employeeId}</span>}
                    </div>
                  </div>
                ))
            )}
          </div>
        </div>
        
        {/* Documents Content */}
        <div className="documents-content">
          {selectedEmployee ? (
            <>
              <div className="content-header">
                <h2>{selectedEmployee.name}'s Documents</h2>
                
                <button className="add-document-button" onClick={openUploadPopup}>
                  <FaPlus className="icon" />
                  <span>Add Document</span>
                </button>
              </div>
              
              <div className="documents-search">
                <FaSearch className="search-icon" />
                <input 
                  type="text" 
                  placeholder="Search documents..." 
                  onChange={(e) => setSearchQuery(e.target.value)}
                  value={searchQuery}
                />
              </div>
              
              {isLoading ? (
                <div className="loading-documents">
                  <FaSpinner className="spinner" />
                  <span>Loading documents...</span>
                </div>
              ) : (
                <div className="documents-grid">
                  {/* ID & Visa Section */}
                  <div className="document-category">
                    <h3>
                      <FaFolder className="category-icon" />
                      ID & Visa
                    </h3>
                    <div className="document-list">
                      {filterByCategory('ID & Visa').length > 0 ? (
                        filterByCategory('ID & Visa').map(doc => (
                          <div key={doc.id} className="document-card">
                            <div className="document-icon">
                              {getFileIcon(doc.fileType)}
                            </div>
                            <div className="document-info">
                              <h4>{doc.fileRefName}</h4>
                              <span className="document-date">
                                {doc.createdAt?.toDate().toLocaleDateString() || 'Unknown date'}
                              </span>
                            </div>
                            <button 
                              className="download-button"
                              onClick={() => handleDownload(doc.fileURL)}
                              title="Download document"
                            >
                              <FaFileDownload />
                            </button>
                            <button 
                              className="delete-button"
                              onClick={() => handleDeleteDocument(doc)}
                              title="Delete document"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        ))
                      ) : (
                        <div className="no-documents">
                          <p>No ID & Visa documents found.</p>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Employment Document Section */}
                  <div className="document-category">
                    <h3>
                      <FaFolder className="category-icon" />
                      Employment Document
                    </h3>
                    <div className="document-list">
                      {filterByCategory('Employment Document').length > 0 ? (
                        filterByCategory('Employment Document').map(doc => (
                          <div key={doc.id} className="document-card">
                            <div className="document-icon">
                              {getFileIcon(doc.fileType)}
                            </div>
                            <div className="document-info">
                              <h4>{doc.fileRefName}</h4>
                              <span className="document-date">
                                {doc.createdAt?.toDate().toLocaleDateString() || 'Unknown date'}
                              </span>
                            </div>
                            <button 
                              className="download-button"
                              onClick={() => handleDownload(doc.fileURL)}
                              title="Download document"
                            >
                              <FaFileDownload />
                            </button>
                            <button 
                              className="delete-button"
                              onClick={() => handleDeleteDocument(doc)}
                              title="Delete document"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        ))
                      ) : (
                        <div className="no-documents">
                          <p>No Employment documents found.</p>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Miscellaneous Section */}
                  <div className="document-category">
                    <h3>
                      <FaFolder className="category-icon" />
                      Miscellaneous
                    </h3>
                    <div className="document-list">
                      {filterByCategory('Miscellaneous').length > 0 ? (
                        filterByCategory('Miscellaneous').map(doc => (
                          <div key={doc.id} className="document-card">
                            <div className="document-icon">
                              {getFileIcon(doc.fileType)}
                            </div>
                            <div className="document-info">
                              <h4>{doc.fileRefName}</h4>
                              <span className="document-date">
                                {doc.createdAt?.toDate().toLocaleDateString() || 'Unknown date'}
                              </span>
                            </div>
                            <button 
                              className="download-button"
                              onClick={() => handleDownload(doc.fileURL)}
                              title="Download document"
                            >
                              <FaFileDownload />
                            </button>
                            <button 
                              className="delete-button"
                              onClick={() => handleDeleteDocument(doc)}
                              title="Delete document"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        ))
                      ) : (
                        <div className="no-documents">
                          <p>No Miscellaneous documents found.</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="no-selection">
              <FaFolder className="folder-icon" />
              <h3>Select an employee to view and manage their documents</h3>
              <p>Choose an employee from the list on the left to get started.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Documents; 